import React from "react";
import { Card, Text, Badge, Link, makeStyles, tokens } from "@fluentui/react-components";
import {
  Person24Regular,
  Briefcase24Regular,
  Building24Regular,
  Mail20Regular,
  Call20Regular,
  PersonTag20Regular,
  Open20Regular,
} from "@fluentui/react-icons";

const useStyles = makeStyles({
  card: {
    marginTop: "8px",
    padding: "12px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
    flexWrap: "wrap",
  },
  crmLink: {
    marginLeft: "auto",
    flexShrink: 0,
    cursor: "pointer",
    color: tokens.colorBrandForeground1,
    display: "flex",
    alignItems: "center",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  detailRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    color: tokens.colorNeutralForeground2,
  },
  detailIcon: {
    flexShrink: 0,
    fontSize: "16px",
    color: tokens.colorNeutralForeground3,
  },
});

interface ContactCardProps {
  record: any;
  type: "contact" | "lead";
}

const DYNAMICS_URL = "https://westcomncs.crm.dynamics.com";

export const ContactCard: React.FC<ContactCardProps> = ({ record, type }) => {
  const styles = useStyles();
  const isContact = type === "contact";
  const accountName =
    record["_parentcustomerid_value@OData.Community.Display.V1.FormattedValue"] ||
    record["parentcustomerid@OData.Community.Display.V1.FormattedValue"];

  const recordId = isContact ? record.contactid : record.leadid;
  const crmUrl = `${DYNAMICS_URL}/main.aspx?etn=${type}&id=${recordId}&pagetype=entityrecord`;

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        {isContact ? <Person24Regular /> : <Briefcase24Regular />}
        <Text weight="semibold">{record.fullname}</Text>
        <Badge appearance="outline" color={isContact ? "brand" : "warning"} size="small">
          {isContact ? "Contact" : "Lead"}
        </Badge>
        <Link
          href={crmUrl}
          target="_blank"
          className={styles.crmLink}
          title="Open in CRM"
        >
          <Open20Regular />
        </Link>
      </div>
      <div className={styles.details}>
        {record.emailaddress1 && (
          <div className={styles.detailRow}>
            <Mail20Regular className={styles.detailIcon} />
            <Text size={200}>{record.emailaddress1}</Text>
          </div>
        )}
        {record.telephone1 && (
          <div className={styles.detailRow}>
            <Call20Regular className={styles.detailIcon} />
            <Text size={200}>{record.telephone1}</Text>
          </div>
        )}
        {record.jobtitle && (
          <div className={styles.detailRow}>
            <PersonTag20Regular className={styles.detailIcon} />
            <Text size={200}>{record.jobtitle}</Text>
          </div>
        )}
        {isContact && accountName && (
          <div className={styles.detailRow}>
            <Building24Regular className={styles.detailIcon} />
            <Text size={200}>{accountName}</Text>
          </div>
        )}
        {!isContact && record.companyname && (
          <div className={styles.detailRow}>
            <Building24Regular className={styles.detailIcon} />
            <Text size={200}>{record.companyname}</Text>
          </div>
        )}
      </div>
    </Card>
  );
};
